<script>
import aboutPhotoUrl from '@src/assets/images/bowvue_home_page_1.webp'
import { authComputed } from '@state/helpers'
import {
  logoComputed,
  dashboardComputed,
  bowtieLogoComputed,
} from '@utils/themed-image'
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
// Ask bar parked. The floating BowVue AI launcher is the single entry point
// again. Restore this import, the registration below and the markup in the
// template together, and drop `hideChatLauncher` back into this page's route.
// import AskBar from '@components/ask-bar'
import {
  mdiQrcodeScan,
  mdiFileDocumentMultipleOutline,
  mdiShieldCheckOutline,
  mdiCashMultiple,
} from '@mdi/js'

export default {
  metaInfo: {
    // Rendered as "<title> | BowVue". Keep it category-explicit; this is
    // the headline search and answer engines show for the site.
    title: 'Arc Flash & Electrical Safety Compliance Software',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { Layout /*, AskBar */ },
  data() {
    return {
      // Icon + i18n key per card; titles/text resolve at render time so they
      // follow the active locale (see the template's $t on feature.titleKey).
      features: [
        { icon: mdiQrcodeScan, key: 'qrLabels' },
        { icon: mdiFileDocumentMultipleOutline, key: 'records' },
        { icon: mdiShieldCheckOutline, key: 'compliance' },
        { icon: mdiCashMultiple, key: 'savings' },
      ],
      capabilityKeys: [
        'electricalEngineering',
        'arcFlashAnalysis',
        'preventiveMaintenance',
        'netaTesting',
        'infraredThermography',
        'electricalSafetyTraining',
      ],
    }
  },
  computed: {
    ...logoComputed,
    ...dashboardComputed,
    ...bowtieLogoComputed,
    // Static image imports are MODULE scope, and the Options API does not expose
    // module scope to the template: binding them directly left :src undefined
    // and v-img rendered nothing at all. They have to come through here.
    aboutPhotoSrc() {
      return aboutPhotoUrl
    },
    // The about photo's space: 10:9 from a laptop up (lg), where it sits beside
    // the text, which shows the nearly square photo almost the column's whole
    // width (4:3 left about a fifth of it as white space before the text);
    // below that, the photo's own shape (undefined), full width as on a phone.
    aboutPhotoRatio() {
      const display = this.$vuetify && this.$vuetify.display
      return display && display.lgAndUp ? 10 / 9 : undefined
    },
    ...authComputed,
  },
}
</script>

<template>
  <Layout>
    <div class="home-page mx-auto">
      <!-- Ask bar parked; the floating launcher is the only way in again.
           Signed-in visitors only: AskBar renders nothing without entitlement, and
           entitlement is never fetched while signed out. So for the audience this
           marketing page was written for, the hero is still the first thing on it.
      <div class="d-flex justify-center">
        <AskBar class="home-ask-bar" />
      </div>
      -->

      <!-- Hero -->
      <v-row align="center" class="home-hero">
        <v-col cols="12" md="6">
          <v-img
            :src="logoSrc"
            alt="BowVue Risk Management"
            width="320"
            max-width="80%"
            class="mb-8 home-hero-logo"
          />
          <h1 class="text-h4 text-md-h3 font-weight-bold mb-5 home-hero-title">
            {{ $t('home.heroTitle') }}
          </h1>
          <p class="text-body-1 text-medium-emphasis mb-8 home-hero-subtitle">
            {{ $t('home.heroSubtitle') }}
          </p>
          <div class="d-flex flex-wrap ga-3">
            <v-btn
              v-if="loggedIn"
              color="brand"
              size="large"
              variant="flat"
              prepend-icon="$dashboard"
              :to="{ name: 'dashboard' }"
            >
              {{ $t('home.goToDashboard') }}
            </v-btn>
            <v-btn
              v-else
              color="brand"
              size="large"
              variant="flat"
              :to="{ name: 'login' }"
            >
              {{ $t('common.logIn') }}
            </v-btn>
            <v-btn
              color="brand"
              size="large"
              variant="outlined"
              :to="{ name: 'contact us' }"
            >
              {{ $t('common.contactUs') }}
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <v-card variant="flat" border rounded="lg" class="home-hero-shot">
            <!-- A picture of the app, so it follows the theme: a light
                 screenshot sitting in a dark page reads as a bright slab and
                 misrepresents what the product looks like. -->
            <v-img :src="dashboardShotSrc" alt="The BowVue dashboard" cover />
          </v-card>
        </v-col>
      </v-row>

      <!-- Feature highlights -->
      <div class="text-center mt-16 mb-8">
        <h2 class="text-h5 text-md-h4 font-weight-bold home-section-title">
          {{ $t('home.featuresTitle') }}
        </h2>
        <p class="text-body-1 text-medium-emphasis mt-2 home-section-subtitle">
          {{ $t('home.featuresSubtitle') }}
        </p>
      </div>
      <v-row>
        <v-col
          v-for="feature in features"
          :key="feature.key"
          cols="12"
          sm="6"
          lg="3"
        >
          <v-card variant="flat" border rounded="lg" class="fill-height">
            <v-card-text class="pa-6 pa-lg-8">
              <v-avatar color="brand" variant="tonal" rounded="lg" size="56">
                <v-icon :icon="feature.icon" size="28" />
              </v-avatar>
              <div
                class="text-h6 font-weight-bold mt-4 mb-2 home-feature-title"
              >
                {{ $t(`home.features.${feature.key}.title`) }}
              </div>
              <div class="text-body-2 text-medium-emphasis feature-text">
                {{ $t(`home.features.${feature.key}.text`) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- About Bowtie Engineering -->
      <v-card variant="flat" border rounded="lg" class="mt-16">
        <v-row align="center" no-gutters>
          <!-- A 10:9 space a third of the card wide from a laptop up
               (aboutPhotoRatio): the photo
               is nearly square, and at 5/12 of a wide page it stood about twice
               as tall as the text beside it. The whole photo is shown in that
               space (contain, not cover), against the card's left edge: its own
               white border is the space above and below it, as in production,
               and cropping took that border off. -->
          <v-col cols="12" md="5" lg="4">
            <v-img
              :src="aboutPhotoSrc"
              alt="Bowtie Engineering technicians at work"
              :aspect-ratio="aboutPhotoRatio"
              position="left center"
              class="home-about-image"
            />
          </v-col>
          <v-col cols="12" md="7" lg="8">
            <div class="pa-8 pa-lg-12 home-about-copy">
              <v-img
                :src="bowtieLogoSrc"
                alt="Bowtie Engineering"
                class="mb-4 home-about-logo"
              />
              <h2 class="text-h5 font-weight-bold mb-3 home-about-title">
                {{ $t('home.aboutTitle') }}
              </h2>
              <!-- The John Welch link sits mid-sentence, so use <i18n-t> with a
                   named slot for {name} rather than splitting the paragraph. -->
              <i18n-t
                keypath="home.aboutParagraph1"
                tag="p"
                scope="global"
                class="text-body-2 text-medium-emphasis mb-3"
              >
                <template v-slot:name>
                  <a
                    href="https://www.linkedin.com/in/john-welch-p-e-cem-60835a8/"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ $t('home.aboutJohnWelch') }}</a
                  >
                </template>
              </i18n-t>
              <p class="text-body-2 text-medium-emphasis mb-4">
                {{ $t('home.aboutParagraph2') }}
              </p>
              <v-chip
                v-for="key in capabilityKeys"
                :key="key"
                variant="tonal"
                color="brand"
                size="small"
                class="mr-2 mb-2"
              >
                {{ $t(`home.capabilities.${key}`) }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Closing call to action.
           brandSurface, not brand: this is a large brand FILL, and `brand` is
           lightened in the dark theme so it can be read as text. White on that
           lightened red is 3.19:1. brandSurface stays the deep #BF0000 in both
           themes, and its on-token supplies the white, so the hardcoded
           text-white classes are gone too. -->
      <v-card
        color="brandSurface"
        variant="flat"
        rounded="lg"
        class="mt-16 mb-4 text-center"
      >
        <v-card-text class="pa-10 pa-lg-14">
          <h2 class="text-h5 text-md-h4 font-weight-bold mb-3 home-cta-title">
            {{ $t('home.ctaTitle') }}
          </h2>
          <p class="text-body-1 mb-8 home-cta-subtitle">
            {{ $t('home.ctaSubtitle') }}
          </p>
          <v-btn
            v-if="loggedIn"
            color="white"
            variant="flat"
            size="large"
            prepend-icon="$dashboard"
            :to="{ name: 'dashboard' }"
          >
            {{ $t('home.openDashboard') }}
          </v-btn>
          <v-btn
            v-else
            color="white"
            variant="flat"
            size="large"
            :to="{ name: 'contact us' }"
          >
            {{ $t('home.getInTouch') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </div>
  </Layout>
</template>

<style scoped>
/* Parked with the ask bar itself.
.home-ask-bar {
  A row of its own, so the component's header-row behaviour does not apply: it
  should not grow to fill, and it has no neighbours to be inset from. The bottom
  margin keeps it off the hero rather than the hero off it. The bar is the thing
  that was added here.
  flex: 0 1 auto;
  width: 100%;
  margin: 0 0 32px;
}
*/

/* Up to 1920px wide, then most of the screen (88% of it, to 3000px) on anything
   wider. A fixed 1920px left a 2880px-wide window (a large monitor, or a window
   zoomed out) with a third of the screen empty around a boxed-in column
   (2026-10-30). The type, logo and cards grow with it from 2560px (the
   min-width: 2560px blocks below) so the wider page is not just wider gaps, and
   the prose keeps its own caps (.home-hero-subtitle, .feature-text,
   .home-cta-subtitle) so no line of it grows past what is comfortable to read. */
.home-page {
  max-width: clamp(1920px, 88vw, 3000px);
}

/* Let the hero own most of the first viewport so the landing feels like a
   full page rather than a strip of content, while never collapsing below its
   natural height on short screens. */
.home-hero {
  padding-top: 24px;
  min-height: min(640px, calc(100vh - 260px));
  align-content: center;
}

.home-hero-subtitle {
  max-width: 620px;
  line-height: 1.7;
}

.feature-text {
  line-height: 1.6;
}

.home-hero-shot {
  overflow: hidden;
}

.home-about-logo {
  width: 220px !important;
}

.home-cta-subtitle {
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
}

/* Text sizes. Every size on the page is the type scale's size for that place,
   times one factor: the page reads about 12% smaller than the app's type scale
   (2026-10-30). Change --home-text to resize all of it together.
   !important throughout: Vuetify's text-* typography utilities are themselves
   !important. */
.home-page {
  --home-text: 0.88;
}

/* text-h4, then text-md-h3, then between h3 and h2 on a large screen. */
.home-hero-title {
  font-size: calc(2.125rem * var(--home-text)) !important;
}

.home-hero-subtitle,
.home-section-subtitle,
.home-cta-subtitle {
  font-size: calc(1rem * var(--home-text)) !important;
}

.home-section-title,
.home-cta-title,
.home-about-title {
  font-size: calc(1.5rem * var(--home-text)) !important;
}

.home-feature-title {
  font-size: calc(1.25rem * var(--home-text)) !important;
}

.feature-text,
.home-about-copy :deep(.text-body-2) {
  font-size: calc(0.875rem * var(--home-text)) !important;
}

.home-about-copy :deep(.v-chip) {
  font-size: calc(0.75rem * var(--home-text)) !important;
}

@media (min-width: 960px) {
  .home-hero-title {
    font-size: calc(3rem * var(--home-text)) !important;
  }

  .home-section-title,
  .home-cta-title {
    font-size: calc(2.125rem * var(--home-text)) !important;
  }
}

/* The about copy grows with the screen, so it keeps up with the photo beside
   it on large monitors. A longer line at a larger size needs more line-height
   too. The logo's width is an inline style from v-img, hence its
   !important. */
@media (min-width: 1280px) {
  .home-hero-title {
    font-size: calc(3.25rem * var(--home-text)) !important;
    line-height: 1.15;
  }

  .home-about-logo {
    width: 260px !important;
  }

  .home-about-title {
    font-size: calc(2rem * var(--home-text)) !important;
    line-height: 1.2;
  }

  /* The copy's column widened with the narrower photo; its lines do not. */
  .home-about-copy :deep(.text-body-2) {
    font-size: calc(1rem * var(--home-text)) !important;
    line-height: 1.7 !important;
    max-width: 820px;
  }
}

@media (min-width: 1920px) {
  .home-about-logo {
    width: 300px !important;
  }

  .home-about-title {
    font-size: calc(2.25rem * var(--home-text)) !important;
  }

  .home-about-copy :deep(.text-body-2) {
    font-size: calc(1.0625rem * var(--home-text)) !important;
  }

  /* The capability chips would read as undersized beside the larger copy. */
  .home-about-copy :deep(.v-chip) {
    font-size: calc(0.875rem * var(--home-text)) !important;
  }
}

/* A 2560px screen and up: the hero, the headings, the cards and the about copy
   at a size that fills the wider page. */
@media (min-width: 2560px) {
  .home-hero {
    min-height: min(820px, calc(100vh - 260px));
  }

  .home-hero-logo {
    width: 420px !important;
  }

  .home-hero-title {
    font-size: calc(4.25rem * var(--home-text)) !important;
  }

  .home-hero-subtitle {
    max-width: 800px;
    font-size: calc(1.3125rem * var(--home-text)) !important;
  }

  .home-section-title,
  .home-cta-title,
  .home-about-title {
    font-size: calc(2.75rem * var(--home-text)) !important;
  }

  .home-section-subtitle {
    font-size: calc(1.25rem * var(--home-text)) !important;
  }

  .home-feature-title {
    font-size: calc(1.5rem * var(--home-text)) !important;
  }

  .feature-text {
    font-size: calc(1.0625rem * var(--home-text)) !important;
  }

  .home-about-logo {
    width: 360px !important;
  }

  .home-about-copy :deep(.text-body-2) {
    font-size: calc(1.25rem * var(--home-text)) !important;
    max-width: 960px;
  }

  .home-about-copy :deep(.v-chip) {
    font-size: calc(1rem * var(--home-text)) !important;
  }

  .home-cta-subtitle {
    max-width: 820px;
    font-size: calc(1.3125rem * var(--home-text)) !important;
  }
}
</style>
